import React, { useEffect, useState } from 'react';
import {
  Accordion,
  Button,
  CardContent,
  InfiniteScroll,
  Input,
  LoadingSpinner,
  ScrollArea,
} from '@ais-ui/ui-core';
import { ReportTemplate, useGetReportTemplates } from '@ais-ui/websockets';
import { useLocalization } from '@ais-ui/locale-management';
import { NoItemsEmptyStateList } from '../../components/EmptyList';
import { CreateReportProvider } from './Mutations/CreateReportTemplate/CreateReportContext';
import { CreateReportTemplate } from './Mutations/CreateReportTemplate/CreateReportTemplate';
import { useReportContext } from './ReportContext';
import { useReportFilters } from './useReportFilters';
import { ReportSort } from './ReportSort';
import { ReportFilter } from './ReportFilter';
import { ReportTemplateItem } from './ReportTemplateItem';

export const ReportListContent = () => {
  const { messages } = useLocalization();
  const [search, setSearch] = useState<string>('');
  const { shouldRefetch, setShouldRefetch } = useReportContext();

  const { data: reportTemplates, isInitialLoading } =
    useGetReportTemplates(shouldRefetch);

  const {
    filteredAndSortedReports,
    reportFilters,
    setReportFilters,
    sortConfig,
    setSortConfig,
  } = useReportFilters(reportTemplates || [], search);

  useEffect(() => {
    const isAnyPendingOrExecuting = !!reportTemplates?.some(
      (reportTemplate) =>
        reportTemplate.tplneweststatus === 'PENDING' ||
        reportTemplate.tplneweststatus === 'EXECUTING'
    );
    setShouldRefetch(isAnyPendingOrExecuting);
  }, [reportTemplates, setShouldRefetch]);

  return (
    <>
      {/* Controls section - outside CardContent to avoid overflow-hidden */}
      <div className="px-6 pb-4">
        <div className="flex gap-2">
          <Input
            id="search"
            placeholder={messages.reportsListSearchPlaceholder}
            className="h-10 flex-1"
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
          />
          <ReportSort sortConfig={sortConfig} setSortConfig={setSortConfig} />
          <ReportFilter
            reportFilters={reportFilters}
            setReportFilters={setReportFilters}
            reportTemplates={reportTemplates}
          />
        </div>
      </div>

      {/* Content section */}
      <CardContent className="flex-1 flex flex-col overflow-hidden pt-0">
        {!isInitialLoading && reportTemplates?.length === 0 && (
          <NoItemsEmptyStateList entity="report" srcImage="empty_list_bro.png">
            <CreateReportProvider>
              <CreateReportTemplate>
                <Button className="w-full h-10">Create report</Button>
              </CreateReportTemplate>
            </CreateReportProvider>
          </NoItemsEmptyStateList>
        )}

        {isInitialLoading && <LoadingSpinner />}

        {!isInitialLoading && reportTemplates?.length !== 0 && (
          <Accordion
            className="flex-1 flex flex-col overflow-hidden"
            type="single"
            collapsible
          >
            <InfiniteScroll
              items={filteredAndSortedReports}
              limit={30}
              observeResize={true}
              className="flex-grow p-0 overflow-y-auto border rounded-lg"
              renderItem={(reportTemplate: ReportTemplate) => {
                return (
                  <ReportTemplateItem
                    key={reportTemplate.tplUuid}
                    reportTemplate={reportTemplate}
                  />
                );
              }}
            />
          </Accordion>
        )}
      </CardContent>
    </>
  );
};
